import { getApplicableCoupons } from '../utils';
import { JbCartItem } from '@jbhi-fi/apis/src/shopify/types/JbCart';
import { Coupon } from '@jbhi-fi/components/src/types';
import { useEffect, useState } from 'react';
import { ProductExclusions } from '../types';
import { Exclusions } from '@jbhi-fi/apis/src/onlineCouponsApi/types';

const getIsExcluded = (
  activeSpendAndGetCoupon: Coupon,
  addedItem: JbCartItem,
  exclusionsMap: Map<string, Exclusions>
): boolean => {
  const { Department, ProductGroup, Product } = addedItem.onlineProduct || {};
  const isPreOrder: boolean = Product?.Flags?.some(flag => flag.Name === 'Pre Order') ?? false;
  const isPreOrderClickAndCollect: boolean =
    Product?.Flags?.some(flag => flag.Name === 'Pre Order Click & Collect') ?? false;
  if (!activeSpendAndGetCoupon) return false;

  const validCoupons = getApplicableCoupons([activeSpendAndGetCoupon], exclusionsMap, {
    supplierNumber: Product?.SupplierProductDetails?.[0]?.SupplierNumber.toString() ?? '',
    departmentCode: Department?.Id.toString() ?? '',
    productGroupId: ProductGroup?.Id.toString() ?? '',
    sku: addedItem.sku,
    isPreOrder: isPreOrder || isPreOrderClickAndCollect,
    isMarketplace: !!addedItem.properties._marketplaceSeller,
  });
  const hasValidCoupons =
    validCoupons.length > 0 &&
    (activeSpendAndGetCoupon?.applicableLineItemIds?.includes(addedItem.key) || activeSpendAndGetCoupon.success);

  return !hasValidCoupons;
};

export const calculateSpendAndGetProgress = (
  activeSpendAndGetCoupon: Coupon | null,
  exclusionsMap?: Map<string, Exclusions>,
  cartItems?: Array<JbCartItem>
) => {
  if (!activeSpendAndGetCoupon || !cartItems?.length || !exclusionsMap) {
    return 0;
  }

  let total = 0;
  for (const item of cartItems) {
    const isExcluded = getIsExcluded(activeSpendAndGetCoupon, item, exclusionsMap);
    if (!isExcluded) {
      total += (item.line_price / 100) * item.quantity;
    }
  }
  return total;
};

export const useSpendAndGetProgress = (
  cartItems: Array<JbCartItem> | undefined,
  activeSpendAndGetCoupon: Coupon | null,
  exclusionMap?: Map<string, Exclusions>,
  product?: ProductExclusions
) => {
  const [progress, setProgress] = useState<number>(0);
  const [isProductExcluded, setIsProductExcluded] = useState<boolean>(false);

  const hasProduct = !!product && product.sku !== '0';

  const applicableCoupons =
    exclusionMap && hasProduct && activeSpendAndGetCoupon
      ? getApplicableCoupons([activeSpendAndGetCoupon], exclusionMap, product)
      : [];

  useEffect(() => {
    setProgress(calculateSpendAndGetProgress(activeSpendAndGetCoupon, exclusionMap, cartItems));
    if (hasProduct && exclusionMap && activeSpendAndGetCoupon) {
      setIsProductExcluded(!applicableCoupons.length);
    }
  }, [activeSpendAndGetCoupon, cartItems, applicableCoupons, product]);

  return { progress, isProductExcluded };
};
